import type { LineBufferState } from "@/lib/terminal-line-editor";
import { terminalStringWidth } from "@/lib/terminal-wcwidth";

export interface CompletionTarget {
	/** 置換対象スパン `[start, cursor)` の生の文字列。バックスラッシュエスケープを含む。 */
	word: string;
	start: number;
}

export type CompletionKind = "file" | "command";

/** シェルに解釈させないためバックスラッシュを付ける文字。 */
const ESCAPED_CHARACTERS = new Set(" \t\n\"'`$&|;()<>*?[]#~{}!\\".split(""));

/**
 * 直後がコマンド位置になる文字。`&&` `||` `$(` は各文字が区切りなので個別に挙げない。
 * バックティックは開始・終了で同じ文字を使うため、この集合には含めず個別に扱う。
 */
const COMMAND_SEPARATORS = new Set("|;&(".split(""));

const MENU_MAX_VISIBLE = 30;
const MENU_COLUMN_GAP = 2;
const MENU_OVERFLOW_NOTICE = "…ほか候補あり (Tabで巡回)";

/**
 * カーソル左側の補完対象語を切り出す。単語の境界はエスケープされていない空白で、カーソル以降の
 * 文字列は置換対象に含めない。
 */
export function extractCompletionTarget(text: string, cursor: number): CompletionTarget {
	const end = Math.max(0, Math.min(cursor, text.length));
	let start = 0;
	let index = 0;
	while (index < end) {
		if (text[index] === "\\") {
			index += 2;
			continue;
		}
		if (/\s/u.test(text[index])) start = index + 1;
		index += 1;
	}
	return { word: text.slice(start, end), start };
}

/**
 * 補完対象語をコマンド名として扱うか判定する。行頭とエスケープされていない区切り文字の直後を
 * コマンド位置とみなす。bash と同じく、`/` を含む語はコマンド位置でもファイル名補完にする
 * (アンエスケープはバックスラッシュを外すだけで `/` を増減させないため生の語で判定できる)。
 * 区切り文字は語の手前までしか見ないため、`$(da` のような語頭に密着した形は語ごとファイル名扱い。
 */
export function detectCompletionKind(text: string, target: CompletionTarget): CompletionKind {
	if (target.word.includes("/")) return "file";

	let commandPosition = true;
	let insideBacktickSubstitution = false;
	let index = 0;
	while (index < target.start) {
		const character = text[index];
		if (character === "\\") {
			commandPosition = false;
			index += 2;
			continue;
		}
		if (character === "`") {
			insideBacktickSubstitution = !insideBacktickSubstitution;
			commandPosition = insideBacktickSubstitution;
		} else if (COMMAND_SEPARATORS.has(character)) commandPosition = true;
		else if (!/\s/u.test(character)) commandPosition = false;
		index += 1;
	}
	return commandPosition ? "command" : "file";
}

/** エスケープを外して実際のファイル名に戻す。末尾の単独バックスラッシュは捨てる。 */
export function unescapeCompletionWord(word: string): string {
	let result = "";
	for (let index = 0; index < word.length; index += 1) {
		if (word[index] !== "\\") {
			result += word[index];
			continue;
		}
		if (index + 1 < word.length) result += word[index + 1];
		index += 1;
	}
	return result;
}

/** ファイル名をコマンドラインへ差し込める形へエスケープする。 */
export function escapeCompletionCandidate(candidate: string): string {
	let result = "";
	for (const character of candidate) {
		if (ESCAPED_CHARACTERS.has(character)) result += "\\";
		result += character;
	}
	return result;
}

/** 候補すべてに共通する先頭部分を返す。 */
export function commonPrefix(candidates: readonly string[]): string {
	if (candidates.length === 0) return "";
	let prefix = candidates[0];
	for (const candidate of candidates.slice(1)) {
		let length = 0;
		while (
			length < prefix.length &&
			length < candidate.length &&
			prefix[length] === candidate[length]
		) {
			length += 1;
		}
		prefix = prefix.slice(0, length);
	}
	return prefix;
}

function replaceSpan(
	text: string,
	start: number,
	cursorEnd: number,
	replacement: string,
): LineBufferState {
	return {
		text: text.slice(0, start) + replacement + text.slice(cursorEnd),
		cursor: start + replacement.length,
	};
}

/** 候補を確定する。ディレクトリ (末尾が "/") 以外は続けて入力できるよう空白を1つ足す。 */
export function applyCandidate(
	text: string,
	start: number,
	cursorEnd: number,
	candidate: string,
): LineBufferState {
	const escaped = escapeCompletionCandidate(candidate);
	return replaceSpan(text, start, cursorEnd, candidate.endsWith("/") ? escaped : `${escaped} `);
}

/** 候補の共通プレフィックスまで伸ばす。確定ではないため空白を足さない。 */
export function applyCommonPrefix(
	text: string,
	start: number,
	cursorEnd: number,
	prefix: string,
): LineBufferState {
	return replaceSpan(text, start, cursorEnd, escapeCompletionCandidate(prefix));
}

function truncateToWidth(text: string, maxWidth: number): string {
	let result = "";
	let width = 0;
	for (const character of text) {
		const nextWidth = width + terminalStringWidth(character);
		if (nextWidth > maxWidth) break;
		result += character;
		width = nextWidth;
	}
	return result;
}

/**
 * 候補一覧を入力行の下に表示する行へ整形する。各行は pending wrap を避けるため cols−1 桁までに
 * 切り詰める。表示は先頭 30 件までで、残りは巡回で辿れることを最終行で知らせる。
 */
export function formatCompletionMenuLines(candidates: readonly string[], cols: number): string[] {
	const terminalCols = Math.max(1, Math.floor(cols));
	const maxWidth = terminalCols - 1;
	const visible = candidates.slice(0, MENU_MAX_VISIBLE);
	if (visible.length === 0 || maxWidth <= 0) return [];

	const columnWidth = Math.max(...visible.map(terminalStringWidth)) + MENU_COLUMN_GAP;
	const columns = Math.max(1, Math.floor(terminalCols / columnWidth));
	const lines: string[] = [];
	for (let index = 0; index < visible.length; index += columns) {
		const row = visible.slice(index, index + columns);
		let line = "";
		for (const [position, candidate] of row.entries()) {
			line += candidate;
			if (position < row.length - 1) {
				line += " ".repeat(columnWidth - terminalStringWidth(candidate));
			}
		}
		lines.push(truncateToWidth(line, maxWidth));
	}
	if (candidates.length > visible.length) {
		lines.push(truncateToWidth(MENU_OVERFLOW_NOTICE, maxWidth));
	}
	return lines;
}
